import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, MapPin, Calendar, ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Reveal } from "@/components/Motion";

const API = process.env.REACT_APP_BACKEND_URL;

const CALENDLY_URL = "https://calendly.com/r-cassilde/30min";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Merci de renseigner tous les champs.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("send failed");
      toast.success("Message reçu. Nous vous répondrons sous 48h ouvrées.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Une erreur est survenue. Merci de réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-32 pb-24" data-testid="contact-page">
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <Reveal>
          <p className="text-xs uppercase tracking-luxe text-gold mb-6">Nous écrire</p>
          <h1 className="font-serif text-5xl md:text-6xl text-white mb-4">Contact</h1>
          <p className="text-white/50 max-w-2xl mb-14 font-light">
            Une question sur nos accompagnements, un partenariat, une demande presse ? Écrivez-nous — nous répondons personnellement sous 48h ouvrées.
          </p>
        </Reveal>

        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Coordonnées */}
          <motion.aside
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
            className="lg:col-span-2 space-y-10"
          >
            <div>
              <p className="text-xs uppercase tracking-luxe text-gold mb-4">Coordonnées</p>
              <div className="space-y-4 text-white/70 font-light">
                <div className="flex items-start gap-3">
                  <Mail size={18} strokeWidth={1.5} className="text-gold mt-1 shrink-0" />
                  <a href="mailto:gestion.svh@serenite-essence.fr" className="hover:text-white break-all">
                    gestion.svh@serenite-essence.fr
                  </a>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin size={18} strokeWidth={1.5} className="text-gold mt-1 shrink-0" />
                  <span>55 route de la Démarche<br/>97233 Schœlcher, Martinique</span>
                </div>
              </div>
            </div>

            <div>
              <p className="text-xs uppercase tracking-luxe text-gold mb-4">Prendre rendez-vous</p>
              <p className="text-white/60 font-light mb-4 text-sm">
                Réservez un créneau direct dans l'agenda de Rodrigue CASSILDÉ.
              </p>
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-calendly"
                className="inline-flex items-center gap-2 border border-gold/60 text-gold hover:bg-gold hover:text-brand-ink transition-colors px-6 py-3 text-xs uppercase tracking-[0.15em] font-semibold"
              >
                <Calendar size={16} strokeWidth={1.5} /> Réserver un appel <ArrowUpRight size={14} />
              </a>
            </div>

            <div className="border-t border-white/10 pt-8">
              <p className="text-xs text-white/40 leading-relaxed">
                SYNERGIE VITA HOLDING (SAS) — Capital 500 €<br/>
                SIREN 108 849 241 — RCS Fort-de-France
              </p>
            </div>
          </motion.aside>

          {/* Formulaire */}
          <motion.form
            onSubmit={submit}
            data-testid="contact-form"
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15 }}
            className="lg:col-span-3 border border-white/10 bg-brand-surface p-8 md:p-10 space-y-6"
          >
            <h2 className="font-serif text-2xl text-white mb-2">Votre message</h2>

            <Field
              label="Nom *"
              testid="contact-name"
              value={form.name}
              onChange={(v) => setForm({ ...form, name: v })}
            />
            <Field
              label="Email *"
              type="email"
              testid="contact-email"
              value={form.email}
              onChange={(v) => setForm({ ...form, email: v })}
            />
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-white/40 mb-2">Message *</label>
              <textarea
                data-testid="contact-message"
                rows={6}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 focus:border-gold rounded-none text-white px-0 py-3 outline-none transition-colors resize-none"
              />
            </div>

            <button
              type="submit"
              data-testid="contact-submit"
              disabled={loading}
              className="w-full bg-gold text-brand-ink hover:bg-brand-gold-hover transition-colors px-8 py-4 text-sm uppercase tracking-[0.15em] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : <>Envoyer <ArrowUpRight size={18} /></>}
            </button>

            <p className="text-xs text-white/30 text-center pt-2">
              Vos données restent confidentielles — voir notre{" "}
              <a href="/confidentialite" className="text-gold/70 hover:text-gold">politique de confidentialité</a>.
            </p>
          </motion.form>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", testid }) => (
  <div>
    <label className="block text-xs uppercase tracking-[0.15em] text-white/40 mb-2">{label}</label>
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-transparent border-b border-white/20 focus:border-gold rounded-none text-white px-0 py-3 outline-none transition-colors"
    />
  </div>
);
